import React, { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import AdminShell from "./AdminShell";
import { getDataAPIAuth, postDataAPI, putDataAPI } from "../../utils/API";
import { dialog } from "../Common/TreasuryDialog";

const GOLD   = "#f8b602";
const GREEN  = "#0e3725";
const ACCENT = "#7c3aed";

// ─── Helpers ──────────────────────────────────────────────────────────────────
const StatusBadge = ({ status }) => {
  const colors = {
    active:    { bg: "#d1fae5", color: "#065f46" },
    pending:   { bg: "#fef3c7", color: "#92400e" },
    inactive:  { bg: "#f3f4f6", color: "#6b7280" },
    failed:    { bg: "#fee2e2", color: "#991b1b" },
    received:  { bg: "#dbeafe", color: "#1e40af" },
    verified:  { bg: "#e0e7ff", color: "#3730a3" },
    credited:  { bg: "#d1fae5", color: "#065f46" },
    rejected:  { bg: "#fee2e2", color: "#991b1b" },
    duplicate: { bg: "#f3f4f6", color: "#6b7280" },
  };
  const s = colors[status] || { bg: "#f3f4f6", color: "#6b7280" };
  return (
    <span style={{
      display: "inline-block", padding: "2px 10px", borderRadius: 12,
      fontSize: 11, fontWeight: 700, background: s.bg, color: s.color, textTransform: "uppercase",
    }}>{status}</span>
  );
};

const SAR = (amount) =>
  new Intl.NumberFormat("en-SA", { style: "currency", currency: "SAR", maximumFractionDigits: 2 }).format(amount || 0);

const fmt = (d) =>
  d ? new Date(d).toLocaleDateString("en-SA", { year: "numeric", month: "short", day: "numeric" }) : "—";

const CopyButton = ({ text }) => {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };
  return (
    <button onClick={copy} style={{
      padding: "3px 10px", borderRadius: 6, border: "1px solid #d1d5db",
      background: copied ? "#d1fae5" : "#f9fafb", color: copied ? "#065f46" : "#374151",
      fontSize: 11, fontWeight: 600, cursor: "pointer", marginLeft: 8,
    }}>
      {copied ? "Copied!" : "Copy"}
    </button>
  );
};

// ─── Field with inline hint ───────────────────────────────────────────────────
const Field = ({ label, hint, apiKey, value, onChange, type = "text", placeholder = "", mono = false, required = false }) => {
  const [show, setShow] = useState(false);
  const isPassword = type === "password";
  const filled = !!value && value.trim() !== "";
  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 5 }}>
        <label style={{ fontSize: 12, fontWeight: 700, color: "#374151" }}>{label}</label>
        {required && <span style={{ fontSize: 10, color: "#ef4444", fontWeight: 700 }}>REQUIRED</span>}
        {filled && <span style={{ fontSize: 10, color: "#065f46", fontWeight: 700 }}>✓ SET</span>}
        {apiKey && (
          <span style={{
            fontSize: 10, background: "#f3f4f6", color: "#6b7280",
            padding: "1px 6px", borderRadius: 4, fontFamily: "monospace",
          }}>{apiKey}</span>
        )}
      </div>
      {hint && <p style={{ margin: "0 0 6px", fontSize: 11, color: "#6b7280", lineHeight: 1.5 }}>{hint}</p>}
      <div style={{ position: "relative" }}>
        <input
          type={isPassword && !show ? "password" : "text"}
          value={value || ""}
          onChange={e => onChange(e.target.value)}
          placeholder={placeholder}
          style={{
            width: "100%", padding: isPassword ? "10px 40px 10px 14px" : "10px 14px",
            border: `1px solid ${filled ? "#a7f3d0" : "#d1d5db"}`,
            borderRadius: 8, fontSize: 13, color: "#111827",
            background: filled ? "#f0fdf4" : "#fff",
            outline: "none", boxSizing: "border-box",
            fontFamily: mono ? "monospace" : "inherit",
          }}
        />
        {isPassword && (
          <button
            type="button"
            onClick={() => setShow(s => !s)}
            style={{
              position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)",
              background: "none", border: "none", cursor: "pointer", color: "#6b7280", fontSize: 16,
            }}
          >
            {show ? "🙈" : "👁"}
          </button>
        )}
      </div>
    </div>
  );
};

// ─── Tab: Configuration ───────────────────────────────────────────────────────
const ConfigTab = ({ token }) => {
  const ENVS = {
    uat:  { label: "UAT / Sandbox", baseUrl: "https://apis.uat.b2b.alinma.com/uat-org/partners" },
    prod: { label: "Production",    baseUrl: "https://apis.b2b.alinma.com/partners" },
  };

  const [config, setConfig] = useState({
    clientId: "", clientSecret: "", partnerId: "",
    merchantId: "", serviceId: "",
    baseUrl: ENVS.uat.baseUrl,
    tlsCertificate: "",
    isActive: false, webhookSecret: "",
  });
  const [env, setEnv] = useState("uat");
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [testing, setTesting]   = useState(false);
  const [testSteps, setTestSteps] = useState(null); // array of step results
  const [dirty, setDirty]       = useState(false);

  const set = (key) => (val) => {
    setConfig(c => ({ ...c, [key]: val }));
    setDirty(true);
  };

  const load = useCallback(async () => {
    try {
      const res = await getDataAPIAuth("admin/alinma/config", token);
      if (res.data.status === 1) {
        const d = res.data.data;
        setConfig(c => ({ ...c, ...d }));
        // Detect env from baseUrl
        if (d.baseUrl && d.baseUrl.includes("uat")) setEnv("uat");
        else if (d.baseUrl) setEnv("prod");
      }
    } catch (e) {}
    setLoading(false);
    setDirty(false);
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const switchEnv = (e) => {
    setEnv(e);
    setConfig(c => ({ ...c, baseUrl: ENVS[e].baseUrl }));
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await putDataAPI("admin/alinma/config", config, token);
      if (res.data.status === 1) {
        setDirty(false);
        dialog.alert("Saved", "Configuration saved. You can now test the connection.");
        load();
      }
    } catch (e) {
      dialog.alert("Error", e.response?.data?.msg || e.message);
    }
    setSaving(false);
  };

  const testConn = async () => {
    setTesting(true);
    setTestSteps(null);
    try {
      const res = await postDataAPI("admin/alinma/test-connection", {}, token);
      setTestSteps(res.data.steps || [{ label: "Connection", ok: true, msg: res.data.msg }]);
    } catch (e) {
      const msg = e.response?.data?.msg || e.message || "Connection failed";
      const steps = e.response?.data?.steps || [{ label: "Connection", ok: false, msg }];
      setTestSteps(steps);
    }
    setTesting(false);
  };

  // Readiness: all required fields filled
  const required = ["clientId", "clientSecret", "partnerId", "merchantId", "serviceId"];
  const filledCount = required.filter(k => config[k] && config[k].trim()).length;
  const isReady = filledCount === required.length;

  const webhookUrl = "https://platform.treasury.sa/api/alinma/webhook";

  if (loading) return <p style={{ color: "#6b7280", padding: 24 }}>Loading configuration…</p>;

  return (
    <div>
      {/* Readiness banner */}
      <div style={{
        padding: "14px 20px", borderRadius: 10, marginBottom: 24,
        background: isReady ? "#d1fae5" : "#fef3c7",
        border: `1px solid ${isReady ? "#6ee7b7" : "#fcd34d"}`,
        display: "flex", alignItems: "center", gap: 12,
      }}>
        <span style={{ fontSize: 20 }}>{isReady ? "✅" : "⚠️"}</span>
        <div>
          <div style={{ fontWeight: 700, fontSize: 13, color: isReady ? "#065f46" : "#92400e" }}>
            {isReady ? "Ready to activate — all required credentials are set" : `${filledCount} of ${required.length} required credentials filled`}
          </div>
          <div style={{ fontSize: 11, color: isReady ? "#047857" : "#b45309", marginTop: 2 }}>
            {isReady
              ? "Save, run Test Connection, then toggle Active to go live."
              : "Fill all required fields below, then save and test."}
          </div>
        </div>
      </div>

      {/* Environment switcher */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: "#374151", marginBottom: 8 }}>Environment</div>
        <div style={{ display: "flex", gap: 8 }}>
          {Object.entries(ENVS).map(([key, e]) => (
            <button
              key={key}
              onClick={() => switchEnv(key)}
              style={{
                padding: "8px 20px", borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: "pointer",
                border: `2px solid ${env === key ? ACCENT : "#d1d5db"}`,
                background: env === key ? ACCENT : "#fff",
                color: env === key ? "#fff" : "#374151",
              }}
            >
              {e.label}
            </button>
          ))}
        </div>
        <div style={{ marginTop: 8, fontSize: 11, color: "#6b7280", fontFamily: "monospace" }}>
          Base URL: {ENVS[env].baseUrl}
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
        {/* Left: credentials */}
        <div>
          <div style={{ background: "#fff", borderRadius: 12, padding: 24, border: "1px solid #e5e7eb", marginBottom: 20 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
              <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: GREEN }}>OAuth2 Credentials</h3>
              <span style={{ fontSize: 11, color: "#6b7280" }}>Used to obtain Bearer token</span>
            </div>
            <Field
              label="OAuth2 Client ID"
              apiKey="client_id"
              hint="Issued by Alinma after signing the B2B agreement. Used in the OAuth2 client_credentials token request."
              value={config.clientId}
              onChange={set("clientId")}
              placeholder="e.g. treasury-client-001"
              required
            />
            <Field
              label="OAuth2 Client Secret"
              apiKey="client_secret"
              hint="Secret paired with Client ID. Never exposed to the frontend — stored encrypted in MongoDB."
              value={config.clientSecret}
              onChange={set("clientSecret")}
              type="password"
              placeholder="Alinma-issued secret"
              required
            />
          </div>

          <div style={{ background: "#fff", borderRadius: 12, padding: 24, border: "1px solid #e5e7eb" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
              <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: GREEN }}>Request Identifiers</h3>
              <span style={{ fontSize: 11, color: "#6b7280" }}>Sent in every API call</span>
            </div>
            <Field
              label="Partner ID"
              apiKey="RequestHeader.PartnerId"
              hint="Alinma-assigned partner identifier. Appears in the RequestHeader of every API call."
              value={config.partnerId}
              onChange={set("partnerId")}
              placeholder="e.g. 59703"
              required
            />
            <Field
              label="Merchant ID"
              apiKey="CreateVirtualAccountRq.MrchntId"
              hint="Your company/merchant ID assigned by Alinma. Sent in the request body when creating a Virtual IBAN."
              value={config.merchantId}
              onChange={set("merchantId")}
              placeholder="e.g. 19998"
              required
            />
            <Field
              label="Service ID"
              apiKey="CreateVirtualAccountRq.SvcId"
              hint="The specific service/product ID for Virtual Accounts. Provided by Alinma alongside Merchant ID."
              value={config.serviceId}
              onChange={set("serviceId")}
              placeholder="e.g. 100"
              required
            />
          </div>
        </div>

        {/* Right: TLS, webhook, status */}
        <div>
          <div style={{ background: "#fff", borderRadius: 12, padding: 24, border: "1px solid #e5e7eb", marginBottom: 20 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
              <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: GREEN }}>Mutual TLS Certificate</h3>
              <span style={{ fontSize: 10, background: "#fef3c7", color: "#92400e", padding: "2px 8px", borderRadius: 4, fontWeight: 700 }}>REQUIRED BY ALINMA</span>
            </div>
            <p style={{ margin: "0 0 14px", fontSize: 11, color: "#6b7280", lineHeight: 1.6 }}>
              Alinma requires a client TLS certificate on every API call via the <code style={{ background: "#f3f4f6", padding: "1px 4px", borderRadius: 3 }}>X-Client-Certificate</code> header.
              Paste the PEM-encoded certificate issued by Alinma below.
            </p>
            <textarea
              value={config.tlsCertificate || ""}
              onChange={e => { setConfig(c => ({ ...c, tlsCertificate: e.target.value })); setDirty(true); }}
              placeholder={"-----BEGIN CERTIFICATE-----\n...\n-----END CERTIFICATE-----"}
              rows={6}
              style={{
                width: "100%", padding: "10px 14px", border: `1px solid ${config.tlsCertificate ? "#a7f3d0" : "#d1d5db"}`,
                borderRadius: 8, fontSize: 11, color: "#111827", fontFamily: "monospace",
                background: config.tlsCertificate ? "#f0fdf4" : "#f9fafb",
                outline: "none", boxSizing: "border-box", resize: "vertical",
              }}
            />
            {config.tlsCertificate && (
              <div style={{ marginTop: 6, fontSize: 11, color: "#065f46", fontWeight: 600 }}>✓ Certificate is set</div>
            )}
          </div>

          <div style={{ background: "#fff", borderRadius: 12, padding: 24, border: "1px solid #e5e7eb", marginBottom: 20 }}>
            <h3 style={{ margin: "0 0 16px", fontSize: 15, fontWeight: 700, color: GREEN }}>Webhook</h3>
            <Field
              label="Webhook Secret"
              apiKey="HMAC-SHA256"
              hint="Used to verify the signature on incoming Alinma payment notifications. Set the same value in the Alinma developer portal."
              value={config.webhookSecret}
              onChange={set("webhookSecret")}
              type="password"
              placeholder="Random secret string"
            />
            <div style={{ background: "#f0fdf4", borderRadius: 8, padding: 12, fontSize: 11, color: "#166534" }}>
              <div style={{ fontWeight: 700, marginBottom: 4 }}>Register this URL with Alinma:</div>
              <div style={{ display: "flex", alignItems: "center", fontFamily: "monospace", wordBreak: "break-all" }}>
                {webhookUrl}
                <CopyButton text={webhookUrl} />
              </div>
            </div>
          </div>

          <div style={{ background: "#fff", borderRadius: 12, padding: 24, border: "1px solid #e5e7eb" }}>
            <h3 style={{ margin: "0 0 16px", fontSize: 15, fontWeight: 700, color: GREEN }}>Integration Status</h3>

            {/* Active toggle */}
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
              <div
                onClick={() => { setConfig(c => ({ ...c, isActive: !c.isActive })); setDirty(true); }}
                style={{
                  width: 44, height: 24, borderRadius: 12, cursor: "pointer",
                  background: config.isActive ? "#22c55e" : "#d1d5db",
                  position: "relative",
                }}
              >
                <div style={{
                  position: "absolute", top: 2, left: config.isActive ? 22 : 2,
                  width: 20, height: 20, borderRadius: "50%", background: "#fff",
                }} />
              </div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: config.isActive ? "#065f46" : "#374151" }}>
                  {config.isActive ? "Active — Live API calls enabled" : "Inactive — No API calls made"}
                </div>
                <div style={{ fontSize: 11, color: "#6b7280" }}>
                  {config.isActive ? "Disable to pause all Virtual IBAN operations" : "Enable only after testing connection"}
                </div>
              </div>
            </div>

            {/* Test connection steps */}
            {testSteps && (
              <div style={{ marginBottom: 16 }}>
                {testSteps.map((step, i) => (
                  <div key={i} style={{
                    display: "flex", alignItems: "flex-start", gap: 8, padding: "8px 0",
                    borderBottom: i < testSteps.length - 1 ? "1px solid #f3f4f6" : "none",
                  }}>
                    <span style={{ fontSize: 14, marginTop: 1 }}>{step.ok ? "✅" : "❌"}</span>
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 700, color: step.ok ? "#065f46" : "#991b1b" }}>{step.label}</div>
                      <div style={{ fontSize: 11, color: "#6b7280" }}>{step.msg}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <button
              onClick={testConn}
              disabled={testing || !isReady}
              style={{
                width: "100%", padding: "10px 0", borderRadius: 8,
                border: `1px solid ${isReady ? GREEN : "#d1d5db"}`,
                background: "#fff", color: isReady ? GREEN : "#9ca3af",
                fontWeight: 600, fontSize: 13, cursor: isReady ? "pointer" : "not-allowed",
              }}
            >
              {testing ? "Testing connection…" : "Test Connection"}
            </button>
            {!isReady && (
              <p style={{ margin: "6px 0 0", fontSize: 11, color: "#9ca3af", textAlign: "center" }}>
                Fill all required fields first
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Save bar */}
      <div style={{
        marginTop: 24, padding: "16px 20px", background: dirty ? "#fef3c7" : "#f9fafb",
        borderRadius: 10, border: `1px solid ${dirty ? "#fcd34d" : "#e5e7eb"}`,
        display: "flex", justifyContent: "space-between", alignItems: "center",
      }}>
        <span style={{ fontSize: 13, color: dirty ? "#92400e" : "#6b7280", fontWeight: dirty ? 600 : 400 }}>
          {dirty ? "You have unsaved changes" : "Configuration is up to date"}
        </span>
        <button
          onClick={save}
          disabled={saving || !dirty}
          style={{
            padding: "10px 28px", borderRadius: 8, border: "none",
            background: dirty ? GOLD : "#e5e7eb",
            color: dirty ? GREEN : "#9ca3af",
            fontWeight: 700, fontSize: 14,
            cursor: dirty ? "pointer" : "not-allowed",
          }}
        >
          {saving ? "Saving…" : "Save Configuration"}
        </button>
      </div>

      {/* Onboarding checklist */}
      <div style={{ marginTop: 24, background: "#fff", borderRadius: 12, padding: 24, border: "1px solid #e5e7eb" }}>
        <h3 style={{ margin: "0 0 16px", fontSize: 14, fontWeight: 700, color: GREEN }}>Go-Live Checklist</h3>
        {[
          { done: !!config.clientId && !!config.clientSecret, label: "OAuth2 Client ID & Secret received from Alinma" },
          { done: !!config.partnerId && !!config.merchantId && !!config.serviceId, label: "Partner ID, Merchant ID, and Service ID configured" },
          { done: !!config.tlsCertificate, label: "Mutual TLS certificate (PEM) uploaded" },
          { done: !!config.webhookSecret, label: "Webhook secret set and registered with Alinma" },
          { done: env === "prod", label: "Environment switched to Production" },
          { done: !!config.isActive, label: "Integration toggled Active" },
        ].map((item, i) => (
          <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "7px 0", borderBottom: i < 5 ? "1px solid #f3f4f6" : "none" }}>
            <span style={{ fontSize: 16 }}>{item.done ? "✅" : "⬜"}</span>
            <span style={{ fontSize: 13, color: item.done ? "#065f46" : "#374151", fontWeight: item.done ? 600 : 400, textDecoration: item.done ? "none" : "none" }}>
              {item.label}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

// ─── Tab: Virtual IBANs ───────────────────────────────────────────────────────
const VibansTab = ({ token }) => {
  const [ibans, setIbans]           = useState([]);
  const [total, setTotal]           = useState(0);
  const [page, setPage]             = useState(1);
  const [loading, setLoading]       = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm]             = useState({ userId: "", senderIban: "", senderBankName: "" });
  const [creating, setCreating]     = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = `page=${page}&limit=10${statusFilter ? `&status=${statusFilter}` : ""}`;
      const res = await getDataAPIAuth(`admin/alinma/vibans?${params}`, token);
      if (res.data.status === 1) { setIbans(res.data.data); setTotal(res.data.total); }
    } catch (e) {}
    setLoading(false);
  }, [token, page, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const activate = async (id) => {
    if (!await dialog.confirm("Activate", "Activate this Virtual IBAN?")) return;
    try { await postDataAPI(`admin/alinma/vibans/${id}/activate`, {}, token); load(); }
    catch (e) { dialog.alert("Error", e.response?.data?.msg || e.message); }
  };

  const deactivate = async (id) => {
    if (!await dialog.confirm("Deactivate", "Deactivate this Virtual IBAN?")) return;
    try { await postDataAPI(`admin/alinma/vibans/${id}/deactivate`, {}, token); load(); }
    catch (e) { dialog.alert("Error", e.response?.data?.msg || e.message); }
  };

  const create = async () => {
    if (!form.userId) { dialog.alert("Error", "User ID is required"); return; }
    setCreating(true);
    try {
      const res = await postDataAPI("admin/alinma/vibans", form, token);
      if (res.data.status === 1) {
        dialog.alert("Created", res.data.msg);
        setShowCreate(false);
        setForm({ userId: "", senderIban: "", senderBankName: "" });
        load();
      }
    } catch (e) { dialog.alert("Error", e.response?.data?.msg || e.message); }
    setCreating(false);
  };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <div style={{ display: "flex", gap: 8 }}>
          {["", "active", "pending", "inactive", "failed"].map(s => (
            <button key={s} onClick={() => { setStatusFilter(s); setPage(1); }} style={{
              padding: "6px 14px", borderRadius: 20, fontSize: 12, fontWeight: 600,
              border: `1px solid ${statusFilter === s ? GREEN : "#d1d5db"}`,
              background: statusFilter === s ? GREEN : "#fff",
              color: statusFilter === s ? "#fff" : "#374151", cursor: "pointer",
            }}>{s || "All"}</button>
          ))}
        </div>
        <button onClick={() => setShowCreate(true)} style={{
          padding: "8px 20px", borderRadius: 8, border: "none",
          background: GOLD, color: GREEN, fontWeight: 700, fontSize: 13, cursor: "pointer",
        }}>+ Create Virtual IBAN</button>
      </div>

      {showCreate && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
          <div style={{ background: "#fff", borderRadius: 12, padding: 28, width: 440, maxWidth: "90vw" }}>
            <h3 style={{ margin: "0 0 6px", color: GREEN }}>Create Virtual IBAN</h3>
            <p style={{ margin: "0 0 20px", fontSize: 12, color: "#6b7280" }}>
              Calls Alinma POST /Create. The investor's National ID and name are fetched from their Nafath-verified profile.
            </p>
            {[
              { label: "User ID (MongoDB _id)", key: "userId", placeholder: "64abc...", hint: "→ MrchntRef1 in Alinma request" },
              { label: "Sender IBAN (optional whitelist)", key: "senderIban", placeholder: "SA0380000000608010167519", hint: "→ Allowed sender IBAN" },
              { label: "Sender Bank Name", key: "senderBankName", placeholder: "Alinma Bank", hint: "Optional" },
            ].map(f => (
              <div key={f.key} style={{ marginBottom: 14 }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                  <label style={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>{f.label}</label>
                  <span style={{ fontSize: 10, color: "#9ca3af" }}>{f.hint}</span>
                </div>
                <input
                  value={form[f.key]}
                  onChange={e => setForm(c => ({ ...c, [f.key]: e.target.value }))}
                  placeholder={f.placeholder}
                  style={{ width: "100%", padding: "9px 12px", border: "1px solid #d1d5db", borderRadius: 8, fontSize: 13, boxSizing: "border-box" }}
                />
              </div>
            ))}
            <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
              <button onClick={() => setShowCreate(false)} style={{ flex: 1, padding: "10px 0", borderRadius: 8, border: "1px solid #d1d5db", background: "#fff", cursor: "pointer" }}>Cancel</button>
              <button onClick={create} disabled={creating} style={{ flex: 1, padding: "10px 0", borderRadius: 8, border: "none", background: GOLD, color: GREEN, fontWeight: 700, cursor: "pointer" }}>
                {creating ? "Creating…" : "Create"}
              </button>
            </div>
          </div>
        </div>
      )}

      {loading ? <p style={{ color: "#6b7280" }}>Loading…</p> : (
        <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e5e7eb", overflow: "hidden" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "#f9fafb" }}>
                {["Investor", "Virtual IBAN", "Bill Seq #", "Status", "Created", "Actions"].map(h => (
                  <th key={h} style={{ padding: "12px 16px", textAlign: "left", fontSize: 12, fontWeight: 700, color: "#374151", borderBottom: "1px solid #e5e7eb" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {ibans.length === 0 ? (
                <tr><td colSpan={6} style={{ padding: 32, textAlign: "center", color: "#9ca3af" }}>No virtual IBANs found</td></tr>
              ) : ibans.map(v => (
                <tr key={v._id} style={{ borderBottom: "1px solid #f3f4f6" }}>
                  <td style={{ padding: "12px 16px" }}>
                    <div style={{ fontWeight: 600, fontSize: 13 }}>{v.userId?.name || "—"}</div>
                    <div style={{ fontSize: 11, color: "#6b7280" }}>{v.userId?.national_id}</div>
                  </td>
                  <td style={{ padding: "12px 16px", fontSize: 12, fontFamily: "monospace" }}>
                    {v.iban || "—"}
                    {v.iban && <CopyButton text={v.iban} />}
                  </td>
                  <td style={{ padding: "12px 16px", fontSize: 12, color: "#6b7280" }}>{v.billSeqNum || "—"}</td>
                  <td style={{ padding: "12px 16px" }}><StatusBadge status={v.status} /></td>
                  <td style={{ padding: "12px 16px", fontSize: 12, color: "#6b7280" }}>{fmt(v.createdAt)}</td>
                  <td style={{ padding: "12px 16px" }}>
                    {v.status !== "active" && (
                      <button onClick={() => activate(v._id)} style={{ padding: "4px 12px", borderRadius: 6, border: "none", background: "#d1fae5", color: "#065f46", fontWeight: 600, fontSize: 11, cursor: "pointer", marginRight: 6 }}>Activate</button>
                    )}
                    {v.status === "active" && (
                      <button onClick={() => deactivate(v._id)} style={{ padding: "4px 12px", borderRadius: 6, border: "none", background: "#fee2e2", color: "#991b1b", fontWeight: 600, fontSize: 11, cursor: "pointer" }}>Deactivate</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ padding: "12px 16px", display: "flex", justifyContent: "space-between", alignItems: "center", borderTop: "1px solid #e5e7eb" }}>
            <span style={{ fontSize: 12, color: "#6b7280" }}>Total: {total}</span>
            <div style={{ display: "flex", gap: 8 }}>
              <button disabled={page === 1} onClick={() => setPage(p => p - 1)} style={{ padding: "4px 12px", borderRadius: 6, border: "1px solid #d1d5db", background: "#fff", cursor: page === 1 ? "not-allowed" : "pointer", opacity: page === 1 ? 0.5 : 1 }}>‹ Prev</button>
              <span style={{ fontSize: 12, padding: "4px 8px" }}>Page {page}</span>
              <button disabled={ibans.length < 10} onClick={() => setPage(p => p + 1)} style={{ padding: "4px 12px", borderRadius: 6, border: "1px solid #d1d5db", background: "#fff", cursor: ibans.length < 10 ? "not-allowed" : "pointer", opacity: ibans.length < 10 ? 0.5 : 1 }}>Next ›</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Tab: Payment Events ──────────────────────────────────────────────────────
const EventsTab = ({ token }) => {
  const [events, setEvents]         = useState([]);
  const [total, setTotal]           = useState(0);
  const [page, setPage]             = useState(1);
  const [loading, setLoading]       = useState(true);
  const [statusFilter, setStatusFilter] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = `page=${page}&limit=10${statusFilter ? `&status=${statusFilter}` : ""}`;
      const res = await getDataAPIAuth(`admin/alinma/events?${params}`, token);
      if (res.data.status === 1) { setEvents(res.data.data); setTotal(res.data.total); }
    } catch (e) {}
    setLoading(false);
  }, [token, page, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const credit = async (eventId, amount) => {
    if (!await dialog.confirm("Credit Wallet", `Credit investor wallet with ${SAR(amount)}?`)) return;
    try {
      const res = await postDataAPI(`admin/alinma/events/${eventId}/credit`, {}, token);
      if (res.data.status === 1) { dialog.alert("Credited", res.data.msg); load(); }
    } catch (e) { dialog.alert("Error", e.response?.data?.msg || e.message); }
  };

  const reject = async (eventId) => {
    if (!await dialog.confirm("Reject", "Mark this payment as rejected?")) return;
    try {
      await postDataAPI(`admin/alinma/events/${eventId}/reject`, { reason: "Rejected by admin" }, token);
      load();
    } catch (e) { dialog.alert("Error", e.response?.data?.msg || e.message); }
  };

  return (
    <div>
      <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
        {["", "received", "credited", "rejected"].map(s => (
          <button key={s} onClick={() => { setStatusFilter(s); setPage(1); }} style={{
            padding: "6px 14px", borderRadius: 20, fontSize: 12, fontWeight: 600,
            border: `1px solid ${statusFilter === s ? GREEN : "#d1d5db"}`,
            background: statusFilter === s ? GREEN : "#fff",
            color: statusFilter === s ? "#fff" : "#374151", cursor: "pointer",
          }}>{s || "All"}</button>
        ))}
      </div>
      {loading ? <p style={{ color: "#6b7280" }}>Loading…</p> : (
        <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e5e7eb", overflow: "hidden" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "#f9fafb" }}>
                {["Date", "Investor", "Amount (SAR)", "Sender IBAN", "Receiver IBAN", "Status", "Actions"].map(h => (
                  <th key={h} style={{ padding: "12px 16px", textAlign: "left", fontSize: 12, fontWeight: 700, color: "#374151", borderBottom: "1px solid #e5e7eb" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {events.length === 0 ? (
                <tr><td colSpan={7} style={{ padding: 32, textAlign: "center", color: "#9ca3af" }}>No payment events found</td></tr>
              ) : events.map(ev => (
                <tr key={ev._id} style={{ borderBottom: "1px solid #f3f4f6" }}>
                  <td style={{ padding: "12px 16px", fontSize: 12, color: "#6b7280" }}>{fmt(ev.paymentDate || ev.createdAt)}</td>
                  <td style={{ padding: "12px 16px" }}>
                    <div style={{ fontWeight: 600, fontSize: 13 }}>{ev.userId?.name || ev.senderName || "—"}</div>
                    <div style={{ fontSize: 11, color: "#6b7280" }}>{ev.userId?.national_id}</div>
                  </td>
                  <td style={{ padding: "12px 16px", fontWeight: 700, color: GREEN }}>{SAR(ev.amount)}</td>
                  <td style={{ padding: "12px 16px", fontSize: 11, fontFamily: "monospace" }}>{ev.senderIban || "—"}</td>
                  <td style={{ padding: "12px 16px", fontSize: 11, fontFamily: "monospace" }}>{ev.receiverIban || "—"}</td>
                  <td style={{ padding: "12px 16px" }}><StatusBadge status={ev.status} /></td>
                  <td style={{ padding: "12px 16px" }}>
                    {ev.status === "received" && !ev.walletCredited && (
                      <>
                        <button onClick={() => credit(ev._id, ev.amount)} style={{ padding: "4px 10px", borderRadius: 6, border: "none", background: "#d1fae5", color: "#065f46", fontWeight: 600, fontSize: 11, cursor: "pointer", marginRight: 6 }}>Credit</button>
                        <button onClick={() => reject(ev._id)} style={{ padding: "4px 10px", borderRadius: 6, border: "none", background: "#fee2e2", color: "#991b1b", fontWeight: 600, fontSize: 11, cursor: "pointer" }}>Reject</button>
                      </>
                    )}
                    {ev.walletCredited && <span style={{ fontSize: 11, color: "#065f46" }}>✓ Credited</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ padding: "12px 16px", display: "flex", justifyContent: "space-between", alignItems: "center", borderTop: "1px solid #e5e7eb" }}>
            <span style={{ fontSize: 12, color: "#6b7280" }}>Total: {total}</span>
            <div style={{ display: "flex", gap: 8 }}>
              <button disabled={page === 1} onClick={() => setPage(p => p - 1)} style={{ padding: "4px 12px", borderRadius: 6, border: "1px solid #d1d5db", background: "#fff", cursor: page === 1 ? "not-allowed" : "pointer", opacity: page === 1 ? 0.5 : 1 }}>‹ Prev</button>
              <span style={{ fontSize: 12, padding: "4px 8px" }}>Page {page}</span>
              <button disabled={events.length < 10} onClick={() => setPage(p => p + 1)} style={{ padding: "4px 12px", borderRadius: 6, border: "1px solid #d1d5db", background: "#fff", cursor: events.length < 10 ? "not-allowed" : "pointer", opacity: events.length < 10 ? 0.5 : 1 }}>Next ›</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Tab: Statistics ──────────────────────────────────────────────────────────
const StatsTab = ({ token }) => {
  const [stats, setStats]   = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getDataAPIAuth("admin/alinma/stats", token)
      .then(res => { if (res.data.status === 1) setStats(res.data.data); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) return <p style={{ color: "#6b7280" }}>Loading stats…</p>;
  if (!stats)  return <p style={{ color: "#ef4444" }}>Failed to load stats</p>;

  const cards = [
    { label: "Total Virtual IBANs",    value: stats.virtualIbans?.total || 0,   color: GREEN },
    { label: "Active IBANs",           value: stats.virtualIbans?.active || 0,  color: "#065f46" },
    { label: "Pending IBANs",          value: stats.virtualIbans?.pending || 0, color: "#92400e" },
    { label: "Failed IBANs",           value: stats.virtualIbans?.failed || 0,  color: "#991b1b" },
    { label: "Total Payment Events",   value: stats.paymentEvents?.total || 0,  color: GREEN },
    { label: "Credited Events",        value: stats.paymentEvents?.credited || 0, color: "#065f46" },
    { label: "Pending Events",         value: stats.paymentEvents?.pending || 0, color: "#92400e" },
    { label: "Total Credited (SAR)",   value: SAR(stats.paymentEvents?.totalAmountCredited), color: GOLD, large: true },
  ];

  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 16 }}>
      {cards.map(c => (
        <div key={c.label} style={{ background: "#fff", borderRadius: 12, padding: 20, border: "1px solid #e5e7eb" }}>
          <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 8 }}>{c.label}</div>
          <div style={{ fontSize: c.large ? 20 : 28, fontWeight: 800, color: c.color }}>{c.value}</div>
        </div>
      ))}
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const AlinmaAdmin = () => {
  const { auth } = useSelector(s => s);
  const token = auth?.data?.accesstoken || auth?.data?.access_token;
  const [activeTab, setActiveTab] = useState("config");

  const tabs = [
    { id: "config", label: "Configuration" },
    { id: "vibans", label: "Virtual IBANs" },
    { id: "events", label: "Payment Events" },
    { id: "stats",  label: "Statistics" },
  ];

  return (
    <AdminShell pageTitle="Virtual IBAN — Alinma Bank">
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 24, fontSize: 13, color: "#6b7280" }}>
        <Link to="/admin/integrations" style={{ color: "#6b7280", textDecoration: "none" }}>Integrations</Link>
        <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
          <polyline points="9 18 15 12 9 6" />
        </svg>
        <span style={{ color: "#111827", fontWeight: 600 }}>Virtual IBAN — Alinma Bank</span>
      </div>

      <div style={{ display: "flex", gap: 4, marginBottom: 24, borderBottom: "2px solid #e5e7eb" }}>
        {tabs.map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)} style={{
            padding: "10px 20px", border: "none", background: "none", cursor: "pointer",
            fontSize: 13, fontWeight: 600,
            color: activeTab === t.id ? ACCENT : "#6b7280",
            borderBottom: `2px solid ${activeTab === t.id ? ACCENT : "transparent"}`,
            marginBottom: -2,
          }}>{t.label}</button>
        ))}
      </div>

      {activeTab === "config" && <ConfigTab token={token} />}
      {activeTab === "vibans" && <VibansTab token={token} />}
      {activeTab === "events" && <EventsTab token={token} />}
      {activeTab === "stats"  && <StatsTab  token={token} />}
    </AdminShell>
  );
};

export default AlinmaAdmin;
